import { AlertCircle, ListChecks } from "lucide-react";
import type { GiveFeedbackData, LensReview, LensSet, PublishedFeedback } from "../../lib/performance-lens/types.ts";
import styles from "./self-check-page.module.css";

type Props = {
  data: GiveFeedbackData;
  published: PublishedFeedback[];
  publishedError: string;
  onRetry: () => void;
};

const itemKey = (label: string) => label.trim().replace(/\s+/g, " ").toLowerCase();
const score = (value: number | null | undefined) => value == null ? "—" : value.toFixed(2);
const signed = (value: number) => `${value > 0 ? "+" : ""}${value.toFixed(2)}`;

export default function SelfRatingHistory({ data, published, publishedError, onRetry }: Props) {
  const latest = [...published].sort((a, b) => b.cycleNumber - a.cycleNumber)[0];
  const history = data.sets
    .filter(set => set.projectId === 0)
    .map(set => ({ set, review: set.reviews.find(review => review.kind === "SELF") }))
    .filter((entry): entry is { set: LensSet; review: LensReview } => !!entry.review)
    .sort((a, b) => a.set.cycleNumber - b.set.cycleNumber)
    .slice(-6);

  const rows = new Map<string, { label: string; hint: string | null }>();
  const addRow = (label: string, hint: string | null) => {
    const key = itemKey(label);
    if (!rows.has(key)) rows.set(key, { label, hint });
  };
  const current = history.find(entry => entry.set.cycleId === data.currentCycle?.id)?.review;
  current?.questions.forEach(question => addRow(question.label, question.hint));
  latest?.result.items.forEach(item => addRow(item.label, null));
  history.forEach(({ review }) => review.questions.forEach(question => addRow(question.label, question.hint)));

  const publishedItems = new Map(latest?.result.items.map(item => [itemKey(item.label), item]) ?? []);
  const windowStart = latest?.result.windowStartCycle;
  const windowEnd = latest?.result.windowEndCycle;
  const ownEnd = latest?.result.ownCycleNumber;
  const ownStart = ownEnd != null && windowStart != null && windowEnd != null
    ? ownEnd - (windowEnd - windowStart) : null;
  const windowDescription = latest
    ? `your cycles ${ownStart != null && ownStart > 0 ? `${ownStart}–${ownEnd}` : "available so far"} (organisation cycles ${windowStart}–${windowEnd})`
    : "no published cycle yet";
  const windowHeading = latest
    ? ownStart != null && ownStart > 0 ? `C${ownStart}–${ownEnd}` : `Org ${windowStart}–${windowEnd}`
    : "Published";

  return <section className={styles.historyCard} aria-labelledby="self-history-heading">
    <div className={styles.historyTitle}><span className={styles.historyIcon}><ListChecks size={23} aria-hidden="true" /></span><h2 id="self-history-heading">Your self-ratings over time</h2></div>
    {rows.size > 0 && <p className={styles.historyIntro}>Self ratings by cycle, compared with your team's published averages for {windowDescription}. Team values require ratings from at least three teammates.</p>}
    {publishedError && <div className={styles.error} role="alert"><AlertCircle size={18} />{publishedError}<button type="button" onClick={onRetry}>Retry</button></div>}
    {rows.size > 0 && !latest && !publishedError && <p className={styles.historyNotice}>No published team comparison yet. It will appear after a cycle is published.</p>}
    {rows.size > 0 ? <div className={styles.historyScroll} role="region" aria-label="Self ratings by cycle" tabIndex={0}>
      <table className={styles.historyTable}>
        <thead><tr><th scope="col">Item</th>{history.map(({ set }) => <th scope="col" key={set.cycleId}>C{set.ownCycleNumber ?? set.cycleNumber}{set.cycleId === data.currentCycle?.id && <small>now</small>}</th>)}<th scope="col">You<small>{windowHeading}</small></th><th scope="col">Team<small>{windowHeading}</small></th><th scope="col">Gap</th></tr></thead>
        <tbody>{[...rows.entries()].map(([key, row]) => {
          const item = publishedItems.get(key);
          const team = item && !item.hidden ? item.teamAverage : null;
          const self = item?.selfAverage ?? null;
          const gap = self != null && team != null ? self - team : null;
          return <tr key={key}><th scope="row"><strong>{row.label}</strong>{row.hint && <small>{row.hint}</small>}</th>{history.map(({ set, review }) => {
            const rating = review.status === "SUBMITTED" ? review.questions.find(question => itemKey(question.label) === key)?.rating : null;
            return <td key={set.cycleId}>{rating != null ? <span className={`${styles.ratingPill} ${styles[`rating${rating}`]}`}>{rating}</span> : <span className={styles.emptyValue}>—</span>}</td>;
          })}<td className={styles.summaryValue}>{score(self)}</td><td className={styles.summaryValue}>{item?.hidden ? <span className={styles.confidential}>Confidential</span> : score(team)}</td><td className={`${styles.summaryValue} ${gap != null && gap >= 0.5 ? styles.gapHigh : gap != null && gap <= -0.5 ? styles.gapLow : ""}`}>{gap == null ? "—" : signed(gap)}</td></tr>;
        })}</tbody>
      </table>
    </div> : <p className={styles.historyNotice}>{latest || publishedError ? "Your item history will appear when Self Check questions are available." : "No published team comparison yet. Your history will appear once Self Check questions are available and a cycle is published."}</p>}
    {rows.size > 0 && <p className={styles.historyFootnote}>Cycle ratings appear after submission. You and Team use the latest published view; hidden team items have no gap.</p>}
  </section>;
}
